import React, { useState, useEffect, useMemo } from 'react';
import { Shield, Download, Search, AlertTriangle, CheckCircle, Clock, Award } from 'lucide-react';
import api from '../../utils/api';
import clsx from 'clsx';
import { ReportChartRow, BarCard, groupCount } from './ReportCharts';

const STATUS = {
  expired:       { label: 'Expired',        cls: 'bg-red-100 text-red-700 border border-red-200',    icon: AlertTriangle },
  expiring_soon: { label: 'Expiring Soon',   cls: 'bg-amber-100 text-amber-700 border border-amber-200', icon: Clock },
  valid:         { label: 'Valid',           cls: 'bg-emerald-100 text-emerald-700 border border-emerald-200', icon: CheckCircle },
};

function StatCard({ label, value, sub, color }) {
  return (
    <div className="card p-5">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{label}</p>
      <p className={clsx('text-2xl font-bold', color)}>{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function Certifications() {
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [status, setStatus]   = useState('all');

  useEffect(() => {
    api.get('/reports/compliance')
      .then(r => setData(r.data || []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let rows = data;
    if (status !== 'all') rows = rows.filter(r => r.expiry_status === status);
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(r =>
        (r.staff_name || '').toLowerCase().includes(q) ||
        (r.qualification_name || '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [data, status, search]);

  const counts = useMemo(() => ({
    total:         data.length,
    expired:       data.filter(r => r.expiry_status === 'expired').length,
    expiring_soon: data.filter(r => r.expiry_status === 'expiring_soon').length,
    valid:         data.filter(r => r.expiry_status === 'valid').length,
  }), [data]);

  const daysUntil = (dateStr) => {
    if (!dateStr) return null;
    const diff = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
    return diff;
  };

  const exportCSV = () => {
    const headers = ['Staff', 'Qualification', 'Issue Date', 'Expiry Date', 'Days Until Expiry', 'Status'];
    const rows = filtered.map(r => {
      const d = daysUntil(r.expiry_date);
      return [
        r.staff_name || '',
        r.qualification_name || '',
        r.issue_date ? new Date(r.issue_date).toLocaleDateString('en-AU') : '',
        r.expiry_date ? new Date(r.expiry_date).toLocaleDateString('en-AU') : '',
        d !== null ? String(d) : '',
        STATUS[r.expiry_status]?.label || r.expiry_status || '',
      ];
    });
    const csv = [headers, ...rows].map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `certifications_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-600 flex items-center justify-center">
            <Shield size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Certifications</h1>
            <p className="text-sm text-slate-500">Staff qualification expiry and compliance status</p>
          </div>
        </div>
        <button onClick={exportCSV} className="btn-secondary btn-sm gap-1.5">
          <Download size={14}/> Export CSV
        </button>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard label="Total Certifications" value={counts.total} color="text-slate-800" />
        <StatCard label="Expired"        value={counts.expired}       sub="Need renewal" color="text-red-600" />
        <StatCard label="Expiring Soon"  value={counts.expiring_soon} sub="Within 30 days" color="text-amber-600" />
        <StatCard label="Valid"          value={counts.valid}         sub="Up to date" color="text-emerald-600" />
      </div>

      {/* Charts */}
      {data.length > 0 && (
        <ReportChartRow>
          <BarCard title="Certifications by qualification" data={groupCount(data, 'qualification_name', { limit: 10 })} color="#06B6D4" horizontal />
        </ReportChartRow>
      )}

      {/* Filters */}
      <div className="card p-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            className="input-field pl-8 text-sm"
            placeholder="Search staff or qualification…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl">
          {[['all', 'All'], ['expired', 'Expired'], ['expiring_soon', 'Expiring Soon'], ['valid', 'Valid']].map(([v, l]) => (
            <button key={v} onClick={() => setStatus(v)}
              className={clsx('px-3 py-1.5 text-xs font-medium rounded-lg transition-all whitespace-nowrap',
                status === v ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700')}>
              {l}
              {v !== 'all' && (
                <span className={clsx('ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold',
                  v === 'expired' ? 'bg-red-100 text-red-700' :
                  v === 'expiring_soon' ? 'bg-amber-100 text-amber-700' :
                  'bg-emerald-100 text-emerald-700')}>
                  {counts[v]}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-40 text-slate-400 text-sm">Loading certifications…</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 gap-2 text-slate-400">
            <Award size={32} className="opacity-30" />
            <p className="text-sm">No certifications match your filters</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide border-b border-slate-100">
                <th className="px-5 py-3">Staff</th>
                <th className="px-5 py-3">Qualification</th>
                <th className="px-5 py-3">Issue Date</th>
                <th className="px-5 py-3">Expiry Date</th>
                <th className="px-5 py-3">Days Remaining</th>
                <th className="px-5 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filtered.map((row, i) => {
                const s = STATUS[row.expiry_status] || STATUS.valid;
                const days = daysUntil(row.expiry_date);
                return (
                  <tr key={i} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-3.5 font-medium text-slate-800 text-sm">{row.staff_name}</td>
                    <td className="px-5 py-3.5 text-sm text-slate-700">{row.qualification_name}</td>
                    <td className="px-5 py-3.5 text-sm text-slate-500">
                      {row.issue_date ? new Date(row.issue_date).toLocaleDateString('en-AU') : '—'}
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-600">
                      {row.expiry_date ? new Date(row.expiry_date).toLocaleDateString('en-AU') : '—'}
                    </td>
                    <td className="px-5 py-3.5 text-sm">
                      {days === null ? '—' : (
                        <span className={clsx('font-medium',
                          days < 0 ? 'text-red-600' : days <= 30 ? 'text-amber-600' : 'text-slate-600')}>
                          {days < 0 ? `${Math.abs(days)}d overdue` : `${days}d`}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={clsx('inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold', s.cls)}>
                        <s.icon size={10} />
                        {s.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-xs text-slate-400">
        {filtered.length} of {data.length} certifications shown · "Expiring Soon" = within 30 days
      </p>
    </div>
  );
}
